import * as ApiCalls from "../../apiCalls";
import { getTrendIconClass } from "./utils";
import { ConfigurableFilter, DashboardConfig, InsightData } from "../../types/dashboardConfig";
import { WidgetConfig } from "./Dashboard";
import { MonthlyIncidentCount } from "../../types/insightDashboard";
import { FilterMultiSelect, FilterMultiSelectOption } from "./filters/FilterMultiSelect";
import { Period } from "./periods";
import * as ActivityConstants from "../../peo/activityConstants";
import * as Filters from "../../mapFunctions/filters";

interface PeoInsightData {
  focus: PeoPeriodData;
  comparison: PeoPeriodData;
  activityCountByFilterOption: ActivityCountByFilterOption | null;
}

interface ActivityCountByFilterOption {
  outreachTypes: Record<string, number>;
  activities: Record<string, number>;
  receivingWaters: Record<string, number>;
  // Activities with no receiving water recorded, which the Data tab treats as applying to all waters
  activitiesWithoutReceivingWater: number;
}

type FilterOption = { name: string; value: string };

// Wording from Maggie's SLG-5208 feedback
const DOUBLE_COUNTING_NOTE = "Activities with multiple selections count under each one.";
const ROUNDING_NOTE = "Receiving water splits are rounded, so totals may differ slightly.";

interface PeoPeriodData {
  // What the period is called, like "Reporting Year 2026" or "Past 6 Months". The API doesn't
  // send it, so getPeoInsight adds it
  label: string;
  activityCount: number;
  impressionCount: number;
  attendeeCount: number;
  volunteerCount: number;
  materialCount: number;
  targetAudienceMetPercent: number | null;
  targetAudienceAnsweredCount: number;
  topPassiveActivities: CategoryValue[];
  topActiveActivities: CategoryValue[];
  topReceivingWaters: CategoryValue[];
  monthlyImpressionCount: MonthlyIncidentCount[];
  reachByActivityGroup: Reach;
  reachByTargetBehavior: Reach;
  reachByTargetAudience: Reach;
  reachByTargetPollutant: Reach;
  internalTrainingCount: number;
  peopleTrainedCount: number;
  peopleTrainedByType: CategoryValue[];
  peopleTrainedByPermit: CategoryValue[];
}

interface CategoryValue {
  key: string;
  displayName: string;
  value: number;
}

interface Reach {
  impressions: CategoryValue[];
  attendees: CategoryValue[];
}

type PeoCountKey =
  | "activityCount"
  | "impressionCount"
  | "attendeeCount"
  | "volunteerCount"
  | "materialCount"
  | "internalTrainingCount"
  | "peopleTrainedCount";

// More isn't always better (e.g. fewer attendees may mean smaller events), so arrows show the
// direction without a good or bad color
const neutralTrendIconClass = (focusNumber: number | null, comparisonNumber: number | null) =>
  getTrendIconClass(focusNumber, comparisonNumber, true);

const numberCard = (
  title: string,
  description: string,
  countKey: PeoCountKey,
  notes?: string[],
): WidgetConfig => ({
  type: "numberCard",
  props: { title, description, notes },
  getData: (insightData: InsightData) => {
    const data = insightData as unknown as PeoInsightData;
    const focusDataNumber = data.focus?.[countKey] ?? 0;
    const comparisonDataNumber = data.comparison?.[countKey] ?? 0;

    return {
      focusDataNumber,
      comparisonDataNumber,
      focusSubtitle: data.focus.label,
      comparisonSubtitle: data.comparison.label,
      trendIconClass: neutralTrendIconClass(focusDataNumber, comparisonDataNumber),
    };
  },
});

// Almost every answer is "yes", so the percentage alone hides how many activities never answered
const answeredSubtitle = (periodData: PeoPeriodData): string =>
  `${periodData.label} · ${periodData.targetAudienceAnsweredCount} of ${periodData.activityCount} answered`;

// The widgets key their series by focusYear and comparisonYear, which must be fixed names because
// they end up in CSS color variables, and show the labels to the reader
const periodProps = (data: PeoInsightData) => ({
  focusYear: "focus",
  comparisonYear: "comparison",
  focusLabel: data.focus.label,
  comparisonLabel: data.comparison.label,
});

type PeoTopListKey = "topPassiveActivities" | "topActiveActivities" | "topReceivingWaters";

const topList = (
  title: string,
  description: string,
  itemLabel: string,
  topListKey: PeoTopListKey,
  notes: string[],
): WidgetConfig => ({
  type: "topList",
  props: { title, description, itemLabel, notes },
  getData: (insightData: InsightData) => {
    const data = insightData as unknown as PeoInsightData;
    return {
      focusData: data.focus?.[topListKey] ?? [],
      comparisonData: data.comparison?.[topListKey] ?? [],
      ...periodProps(data),
    };
  },
});

type PeoReachKey =
  | "reachByActivityGroup"
  | "reachByTargetBehavior"
  | "reachByTargetAudience"
  | "reachByTargetPollutant";

const reachChart = (
  title: string,
  description: string,
  reachKey: PeoReachKey,
  notes: string[],
): WidgetConfig => ({
  type: "measureToggleBarChart",
  props: { title, description, notes },
  getData: (insightData: InsightData) => {
    const data = insightData as unknown as PeoInsightData;
    return {
      measures: [
        {
          label: "Impressions",
          focusData: data.focus?.[reachKey].impressions ?? [],
          comparisonData: data.comparison?.[reachKey].impressions ?? [],
        },
        {
          label: "Attendees",
          focusData: data.focus?.[reachKey].attendees ?? [],
          comparisonData: data.comparison?.[reachKey].attendees ?? [],
        },
      ],
      ...periodProps(data),
    };
  },
});

// How many activities each option has across both years given the other filters, so the filter
// can hide options that would leave nothing, like the Data tab's smart filters
const withActivityCounts = (
  options: FilterOption[],
  insightData: InsightData,
  countKey: PeoFilterKey,
): FilterMultiSelectOption[] => {
  const countByFilterOption = (insightData as unknown as PeoInsightData)
    .activityCountByFilterOption;
  const counts = countByFilterOption?.[countKey];
  const countForEveryOption =
    countKey === "receivingWaters"
      ? (countByFilterOption?.activitiesWithoutReceivingWater ?? 0)
      : 0;
  return options.map((option) => ({
    ...option,
    count: (counts?.[option.value] ?? 0) + countForEveryOption,
  }));
};

// Same options, in the same order, as the Data tab's Outreach Type and Activity filters
const getOutreachTypeOptions = (): FilterOption[] =>
  [
    ...new Set(
      Object.values(ActivityConstants.get().activities).map(
        (activity: any) => activity.outreachType as string,
      ),
    ),
  ].map((outreachType) => ({ name: outreachType, value: outreachType }));

const getActivityOptions = (): FilterOption[] =>
  Object.entries(ActivityConstants.get().activities).map(([code, activity]: [string, any]) => ({
    name: activity.displayName,
    value: code,
  }));

type PeoFilterKey = "outreachTypes" | "activities" | "receivingWaters";

const allOptionsByFilter: Record<PeoFilterKey, () => FilterOption[]> = {
  outreachTypes: getOutreachTypeOptions,
  activities: getActivityOptions,
  receivingWaters: Filters.getReceivingWaterOptions,
};

type PeoFilters = Record<PeoFilterKey, string[]> & {
  focusPeriod: Period;
  comparisonPeriod: Period;
};

// Like the Data tab, every option starts checked and the filter's state is the checked options
const multiSelectFilter = (
  label: string,
  placeholder: string,
  stateKey: PeoFilterKey,
  getAllOptions: () => FilterOption[],
): ConfigurableFilter => ({
  component: FilterMultiSelect,
  props: { label, placeholder },
  stateKey,
  // A getter, because the receiving waters come from the map and aren't known when this file loads
  get initialValue() {
    return getAllOptions().map((option) => option.value);
  },
  getOptions: (insightData: InsightData) =>
    withActivityCounts(getAllOptions(), insightData, stateKey),
});

// A filter with every option checked, or none, doesn't narrow anything (the Data tab works the same
// way), so the API only gets a filter that is partly checked
const getPeoInsight = async (filters: PeoFilters): Promise<InsightData> => {
  const { focusPeriod, comparisonPeriod } = filters;
  const narrowedFilters = {} as Record<PeoFilterKey, string[]>;
  for (const stateKey of Object.keys(allOptionsByFilter) as PeoFilterKey[]) {
    const checkedValues = filters[stateKey];
    const isNarrowed =
      checkedValues.length > 0 && checkedValues.length < allOptionsByFilter[stateKey]().length;
    narrowedFilters[stateKey] = isNarrowed ? checkedValues : [];
  }
  const insightData = await ApiCalls.getActivityInsight({
    focusStart: focusPeriod.start,
    focusEnd: focusPeriod.end,
    comparisonStart: comparisonPeriod.start,
    comparisonEnd: comparisonPeriod.end,
    ...narrowedFilters,
  });
  // The API answers for the start and end times it was given, so each period's name is added here
  return {
    ...insightData,
    focus: { ...insightData.focus, label: focusPeriod.label },
    comparison: { ...insightData.comparison, label: comparisonPeriod.label },
  };
};

export const peoDashboardConfig: DashboardConfig = {
  dashboardType: "peo",
  getInsightDataFunction: getPeoInsight,
  // PEO annual reports are stored under the "public" data name
  getReportingYearsFunction: async () => ApiCalls.getAnnualReports("public"),
  getFilterOptionsFunction: () => [],
  timePeriodPicker: true,
  widgetConfigs: [
    numberCard(
      "Total Count of Activities",
      "Completed education and outreach activity inventory",
      "activityCount",
    ),
    numberCard("Outreach Impressions", "Outreach impressions to date", "impressionCount", [
      ROUNDING_NOTE,
    ]),
    numberCard("Attendees", "Attendees to date", "attendeeCount", [ROUNDING_NOTE]),
    numberCard("Volunteers", "Volunteers to date", "volunteerCount", [ROUNDING_NOTE]),
    numberCard("Materials Distributed", "Materials distributed to date", "materialCount", [
      ROUNDING_NOTE,
    ]),
    {
      type: "numberCard",
      props: {
        title: "Target Audience Met",
        description: "Percentage of activities where target audience was met",
      },
      getData: (insightData: InsightData) => {
        const data = insightData as unknown as PeoInsightData;
        const focusPercent = data.focus?.targetAudienceMetPercent ?? null;
        const comparisonPercent = data.comparison?.targetAudienceMetPercent ?? null;

        return {
          focusDataNumber: focusPercent ?? "—",
          comparisonDataNumber: comparisonPercent ?? "—",
          focusDataUnit: focusPercent === null ? "" : "%",
          comparisonDataUnit: comparisonPercent === null ? "" : "%",
          focusSubtitle: answeredSubtitle(data.focus),
          comparisonSubtitle: answeredSubtitle(data.comparison),
          trendIconClass: neutralTrendIconClass(focusPercent, comparisonPercent),
        };
      },
    },
    numberCard(
      "Internal Trainings",
      "Completed internal staff trainings inventory",
      "internalTrainingCount",
    ),
    numberCard("People Trained", "People trained to date", "peopleTrainedCount", [ROUNDING_NOTE]),
    {
      type: "monthlyComparisonBarChart",
      props: {
        title: "Monthly Impressions",
        description: "Seasonal progression of outreach impressions",
        notes: [ROUNDING_NOTE],
      },
      getData: (insightData: InsightData) => {
        const data = insightData as unknown as PeoInsightData;
        return {
          focusData: data.focus?.monthlyImpressionCount ?? [],
          comparisonData: data.comparison?.monthlyImpressionCount ?? [],
          ...periodProps(data),
        };
      },
    },
    topList(
      "Top 5 Passive Activities",
      "Total outreach impressions",
      "Activity",
      "topPassiveActivities",
      [ROUNDING_NOTE],
    ),
    topList(
      "Top 5 Active/Interactive Activities",
      "Total attendees",
      "Activity",
      "topActiveActivities",
      [ROUNDING_NOTE],
    ),
    topList(
      "Top 5 Receiving Waters",
      "Total impressions and attendees",
      "Receiving Water",
      "topReceivingWaters",
      [ROUNDING_NOTE],
    ),
    reachChart(
      "Activities by Type",
      "Comparison of activity types by impressions and attendees",
      "reachByActivityGroup",
      [ROUNDING_NOTE],
    ),
    reachChart(
      "Activity Target Behavior",
      "Comparison of behaviors by impressions and attendees",
      "reachByTargetBehavior",
      [DOUBLE_COUNTING_NOTE, ROUNDING_NOTE],
    ),
    reachChart(
      "Activity Target Audience",
      "Comparison of audiences by impressions and attendees",
      "reachByTargetAudience",
      [DOUBLE_COUNTING_NOTE, ROUNDING_NOTE],
    ),
    reachChart(
      "Activity Target Pollutants",
      "Comparison of pollutants by impressions and attendees",
      "reachByTargetPollutant",
      [DOUBLE_COUNTING_NOTE, ROUNDING_NOTE],
    ),
    {
      type: "breakdownBarChart",
      props: {
        title: "Trainings by Type",
        description: "Comparison of training type by people trained",
        notes: [ROUNDING_NOTE],
      },
      getData: (insightData: InsightData) => {
        const data = insightData as unknown as PeoInsightData;
        return {
          focusData: data.focus?.peopleTrainedByType ?? [],
          comparisonData: data.comparison?.peopleTrainedByType ?? [],
          ...periodProps(data),
        };
      },
    },
    {
      type: "horizontalBarChart",
      props: {
        title: "Trainings by Permit Section",
        description: "Comparison of permit sections by people trained",
        notes: [ROUNDING_NOTE],
      },
      getData: (insightData: InsightData) => {
        const data = insightData as unknown as PeoInsightData;
        return {
          focusData: data.focus?.peopleTrainedByPermit ?? [],
          comparisonData: data.comparison?.peopleTrainedByPermit ?? [],
          ...periodProps(data),
        };
      },
    },
  ],
  // Like the Data tab's filters, these apply to outreach and trainings alike
  configurableFilters: [
    multiSelectFilter(
      "Outreach Type",
      "All Outreach Types",
      "outreachTypes",
      getOutreachTypeOptions,
    ),
    multiSelectFilter("Activity", "All Activities", "activities", getActivityOptions),
    multiSelectFilter(
      "Receiving Water",
      "All Receiving Waters",
      "receivingWaters",
      Filters.getReceivingWaterOptions,
    ),
  ],
};
